import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Edit3, Trash2, Save, X, Server } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { seguridadAPI } from './api';
import { toast } from 'sonner';

const TrustedIPsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode: 'create'|'edit', item?}
  const [form, setForm] = useState({ ip_or_cidr: '', label: '' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    seguridadAPI
      .trustedIPs()
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error('Error al cargar IPs'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm({ ip_or_cidr: '', label: '' });
    setModal({ mode: 'create' });
  };

  const openEdit = (item) => {
    setForm({ ip_or_cidr: item.ip_or_cidr, label: item.label });
    setModal({ mode: 'edit', item });
  };

  const closeModal = () => {
    setModal(null);
    setForm({ ip_or_cidr: '', label: '' });
  };

  const validate = () => {
    if (!form.ip_or_cidr.trim() || !form.label.trim()) {
      toast.error('IP/CIDR y etiqueta son obligatorios');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        await seguridadAPI.createTrustedIP(form.ip_or_cidr.trim(), form.label.trim());
        toast.success('IP agregada');
      } else {
        await seguridadAPI.updateTrustedIP(modal.item.id, {
          ip_or_cidr: form.ip_or_cidr.trim(),
          label: form.label.trim(),
        });
        toast.success('IP actualizada');
      }
      closeModal();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await seguridadAPI.updateTrustedIP(item.id, { activo: !item.activo });
      toast.success(item.activo ? 'IP desactivada' : 'IP activada');
      load();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar la IP ${item.ip_or_cidr}?`)) return;
    try {
      await seguridadAPI.deleteTrustedIP(item.id);
      toast.success('IP eliminada');
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-4" data-testid="trusted-ips-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#5A5A5A]">
          IPs y rangos CIDR de la red corporativa que quedan exentos del bloqueo
          por spraying. Los bloqueos por (IP + email) y por email siguen aplicando.
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
          data-testid="btn-add-ip"
        >
          <Plus size={14} className="mr-1.5" />
          Agregar IP
        </Button>
      </div>

      <div className="bg-white border border-[#E8E8E7] rounded-lg overflow-hidden">
        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <Loader2 size={28} className="animate-spin text-[#9d8272]" />
          </div>
        ) : items.length === 0 ? (
          <div className="h-32 flex items-center justify-center text-sm text-[#888888]">
            No hay IPs de confianza registradas.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="trusted-ips-table">
            <thead className="bg-[#fbf7f0] border-b border-[#e8dfd0]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">
                  IP / CIDR
                </th>
                <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">
                  Etiqueta
                </th>
                <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">
                  Estado
                </th>
                <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">
                  Creado
                </th>
                <th className="text-right px-3 py-2 font-semibold text-[#2D2D2D]">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E8E7]">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-[#fafaf8]" data-testid={`ip-row-${it.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 font-mono text-xs">
                      <Server size={12} className="text-[#9d8272]" />
                      {it.ip_or_cidr}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[#2D2D2D]">{it.label}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleActive(it)}
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${
                        it.activo
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-gray-100 text-gray-600 border-gray-200'
                      }`}
                      data-testid={`toggle-${it.id}`}
                    >
                      {it.activo ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-[#5A5A5A]">
                    {new Date(it.created_at).toLocaleDateString('es-PE')}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(it)}
                        data-testid={`edit-${it.id}`}
                      >
                        <Edit3 size={12} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(it)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-${it.id}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-testid="ip-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8E8E7]">
              <h3 className="text-lg font-semibold text-[#2D2D2D]">
                {modal.mode === 'create' ? 'Agregar IP de confianza' : 'Editar IP'}
              </h3>
              <button onClick={closeModal} aria-label="Cerrar">
                <X size={18} className="text-[#888888]" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-[#5A5A5A] block mb-1">
                  IP o CIDR<span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.ip_or_cidr}
                  onChange={(e) => setForm({ ...form, ip_or_cidr: e.target.value })}
                  placeholder="181.177.245.149 o 181.177.245.0/24"
                  className="font-mono text-sm"
                  data-testid="ip-input"
                />
                <p className="text-[10px] text-[#888888] mt-1">
                  Acepta IP única (v4) o rango CIDR (v4).
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5A5A5A] block mb-1">
                  Etiqueta<span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Oficina Lima (principal)"
                  data-testid="label-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#E8E8E7]">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                data-testid="save-ip"
              >
                {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustedIPsTab;
